import {
  format,
  isAfter,
  parseISO,
  startOfMonth,
  addDays,
  isBefore,
  addSeconds,
} from 'date-fns';
import { useMemo } from 'react';
import { Divider, IconButton, Stack, Typography } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { ReactComponent as BatteryIcon } from './icons/battery.svg';
import { ReactComponent as TimeIcon } from './icons/time.svg';
import { ReactComponent as ChargeIcon } from './icons/charge.svg';
import { ReactComponent as PriceIcon } from './icons/price.svg';
import { ReactComponent as TourIcon } from '../tour.svg';
import { ReactComponent as TruckIcon } from '../truck.svg';
import { useTours } from '@energy-stacks/fleet/feature-tours';
import { TourModel } from '@energy-stacks/fleet/feature-tours-data';
import { ESCircularProgress } from '@energy-stacks/core/ui';
import { useGetChargingReservationsQuery } from '@energy-stacks/fleet-is/feature-charging-schedule-data';
import { toPayloadDate } from '@energy-stacks/shared';
import { formatIntervalDuration } from '@energy-stacks/fleet/shared';

export const ScheduleInfo = ({
  vehicleName,
  startDate,
  endDate,
  socStart,
  socEnd,
  chargedEnergy,
  totalPrice,
  onClose,
}: {
  vehicleName: string;
  startDate: Date;
  endDate: Date;
  socStart: number;
  socEnd: number;
  chargedEnergy: number;
  totalPrice: number;
  onClose: () => void;
}) => {
  const { data, isLoading: isLoadingTours, isError: isErrorTours } = useTours();
  const sortedTours = useMemo(() => {
    return [...(data ?? ([] as TourModel[]))].sort((a, b) => {
      const dateA = parseISO(a.startDate);
      const dateB = parseISO(b.startDate);
      return isAfter(dateA, dateB) ? -1 : 1;
    });
  }, [data]);
  //NOTE: Should be moved to BE for increased performance
  const vehicleTours = sortedTours?.filter(
    (tour) =>
      tour.vehicle.name === vehicleName &&
      isAfter(parseISO(tour.startDate), startDate)
  );
  const nextTour = vehicleTours[0];

  const {
    data: reservations,
    isLoading: isReservationsLoading,
    isError: isReservationsError,
  } = useGetChargingReservationsQuery({
    dateTimeFrom: toPayloadDate(startOfMonth(endDate)),
    dateTimeTo: toPayloadDate(addDays(startOfMonth(endDate), 30)),
  });
  const sortedReservations = useMemo(() => {
    return [...(reservations ?? ([] as []))].sort((a, b) => {
      const dateA = parseISO(a.startDate);
      const dateB = parseISO(b.startDate);
      return isAfter(dateA, dateB) ? -1 : 1;
    });
  }, [reservations]);
  //NOTE: Should be moved to BE for increased performance
  const vehicleCharges = sortedReservations?.filter(
    (reservations) =>
      reservations.vehicleDetails.vehicleName === vehicleName &&
      isAfter(parseISO(reservations.startDate), startDate)
  );
  const nextCharge = vehicleCharges[0];

  //find all the tours between current schedule and next charge and sum them up
  const vehicleToursUntilNextCharge =
    (nextCharge &&
      vehicleTours?.filter(
        (tour) =>
          tour.vehicle.name === vehicleName &&
          isBefore(parseISO(tour.endDate), parseISO(nextCharge.startDate))
      )) ??
    [];

  const toursDurationSum = vehicleToursUntilNextCharge
    .map((tour) => tour.duration.time)
    .reduce((acc, next) => acc + next, 0);
  const toursDistanceSum = vehicleToursUntilNextCharge
    .map((tour) => tour.duration.distance)
    .reduce((acc, next) => acc + next, 0);

  return (
    <Stack style={{ position: 'relative' }} direction="column" gap={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography
          fontWeight={500}
          width="90%"
          style={{ wordBreak: 'break-word' }}
          color="#121926"
          variant="h4"
        >
          {vehicleName}
        </Typography>
        <IconButton
          style={{ position: 'absolute', right: -7, top: -7 }}
          onClick={(e) => {
            onClose();
          }}
        >
          <IconX width={14} height={14} stroke={1} />
        </IconButton>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" gap={2}>
          <TimeIcon />
          <Typography fontSize={12}>Start time:</Typography>
        </Stack>
        <Stack>
          <Typography textAlign="right" fontSize={12} mb={1}>
            {format(startDate, 'HH:mmaaa')}
          </Typography>
          <Stack
            bgcolor="primary.light"
            color="primary.light.800"
            direction="row"
            alignItems={'center'}
            gap={2}
            px={1}
          >
            <Typography pt={0.65} fontSize={12}>
              <BatteryIcon />
            </Typography>
            <Typography fontSize={12}>SOC: {socStart}%</Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" textAlign="right" justifyContent="space-between">
        <Stack direction="row" gap={2}>
          <TimeIcon />
          <Typography fontSize={12} mb={1}>
            End time:
          </Typography>
        </Stack>
        <Stack>
          <Typography textAlign="right" fontSize={12} mb={1}>
            {format(endDate, 'HH:mmaaa')}
          </Typography>
          <Stack
            bgcolor="primary.light"
            color="primary.light.800"
            direction="row"
            alignItems={'center'}
            gap={2}
            px={1}
          >
            <Typography pt={0.65} fontSize={12}>
              <BatteryIcon />
            </Typography>
            <Typography fontSize={12}>SOC: {socEnd}%</Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={2}>
          <ChargeIcon />
          <Typography fontSize={12}>Charged Energy:</Typography>
        </Stack>
        <Typography fontSize={12}>{chargedEnergy} KWh</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={2}>
          <PriceIcon />
          <Typography fontSize={12}>Estimated Price(total):</Typography>
        </Stack>
        <Typography fontSize={12}>{totalPrice}€</Typography>
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" gap={2}>
          <TourIcon />
          <Typography fontSize={12}>Next Tour</Typography>
        </Stack>
        <Typography fontSize={12}>
          {isLoadingTours ? (
            <ESCircularProgress size="small" />
          ) : nextTour ? (
            `${format(parseISO(nextTour.startDate), 'HH:mmaaa')} • #${
              nextTour.tourId
            }`
          ) : !isErrorTours ? (
            'No tours planned'
          ) : (
            'N/A'
          )}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" gap={2}>
          <TruckIcon />
          <Typography fontSize={12}>Drive until next charge</Typography>
        </Stack>
        <Typography fontSize={12}>
          {' '}
          {isReservationsLoading || isLoadingTours ? (
            <ESCircularProgress size="small" />
          ) : !isErrorTours && !isReservationsError ? (
            toursDurationSum > 0 ? (
              `${formatIntervalDuration(
                toPayloadDate(new Date()),
                toPayloadDate(addSeconds(new Date(), toursDurationSum))
              )} • ${toursDistanceSum.toFixed(1)}km`
            ) : (
              'No next charge (30 days)'
            )
          ) : (
            'N/A'
          )}
        </Typography>
      </Stack>
    </Stack>
  );
};
